import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ModuleSidebar } from '../../../../components/ui/ModuleSidebar';
import { ProductNavigation } from '../../../../components/ui/ProductNavigation';
import { findProductModule } from '../../../../components/ui/ProductNavigation/navigation-data';
import { Message, MessageStack } from '../../../../components/ui/Message';
import { PageBreadcrumb, PageContentCard } from '../../../../components/ui/ModulePage';
import { demoToday, shiftDays } from '../../../../lib/demoTime';
import { RecommendationCardCreator } from './app/RecommendationCardCreator';
import { RecommendationCardListView } from './app/RecommendationCardListView';
import type { RecommendationCard } from './fixtures';
import { addRecommendationCard, setPendingFocus, useCouponEntities, useCouponStore } from '../../../shared/couponStore';
import styles from './RecommendationCardManagementPage.module.css';

const recruitmentModule = findProductModule('recruitment');

/** 叠加关系统一在优惠券管理的这个页面里配，推荐卡侧只负责跳过去。 */
const stackingRulesPath = '/coupon-management/v1';

export function RecommendationCardManagementV1Page() {
  const navigate = useNavigate();
  const { cards, groups } = useCouponStore();
  const entities = useCouponEntities();
  const [creating, setCreating] = useState(false);
  const [editingCard, setEditingCard] = useState<RecommendationCard | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * 推荐卡里不能配叠加，只能「看」——把要配的那张奖励券记到共享数据里，
   * 跳到叠加规则设置页后，那边在渲染期直接打开规则编辑或关系明细。
   */
  function openStackingSettings(couponId: number) {
    setPendingFocus(couponId, 'rule');
    navigate(stackingRulesPath);
  }

  function openRelationDetail(couponId: number) {
    setPendingFocus(couponId, 'relation');
    navigate(stackingRulesPath);
  }

  function saveCard(name: string) {
    if (editingCard) {
      setEditingCard(null);
      setNotice('电子推荐卡已保存');
      return;
    }
    /* 新建的卡片活动窗口默认从今天起一个月，状态因此落在「进行中」。 */
    const nextId = cards.reduce((maximum, card) => Math.max(maximum, card.id), 0) + 1;
    const card: RecommendationCard = {
      authorizedCoaches: 0,
      endTime: shiftDays(demoToday, 30),
      id: nextId,
      name: name.trim() || `电子推荐卡${nextId}`,
      shareScope: '指定学员',
      startTime: demoToday,
    };
    addRecommendationCard(card);
    setCreating(false);
    /* 提示里保留配置入口的说明：具体配哪张券去列表的「叠加范围」两列点进去。 */
    setNotice(`电子推荐卡“${card.name}”已创建，可在「叠加规则设置」中配置叠加范围`);
  }

  return <main className={styles.page}>
    <ProductNavigation defaultActive="招生营销" />
    <div className={styles.workspace}>
      <ModuleSidebar activeItem="电子推荐卡管理" ariaLabel="招生营销导航" groups={recruitmentModule?.groups ?? []} />
      <section aria-label="电子推荐卡管理页面">
        <h1 className={styles.visuallyHidden}>电子推荐卡管理</h1>
        <div className={styles.kit}>
          <PageBreadcrumb current={editingCard ? '编辑电子推荐卡' : '电子推荐卡管理'} items={['招生营销']} />
          <PageContentCard className={styles.contentCard}>
            <div className={styles.panel}>
              {creating || editingCard
                ? <RecommendationCardCreator
                  card={editingCard ?? undefined}
                  entities={entities}
                  groups={groups}
                  key={editingCard ? `card-${editingCard.id}` : 'new'}
                  onBack={() => { setCreating(false); setEditingCard(null); }}
                  onNotice={setNotice}
                  onOpenRelation={openRelationDetail}
                  onOpenRules={openStackingSettings}
                  onPreview={() => setNotice('预览功能将在后续版本提供')}
                  onSave={saveCard}
                />
                : <RecommendationCardListView onCreate={() => setCreating(true)} onEdit={setEditingCard} onOpenRules={openStackingSettings} />}
            </div>
          </PageContentCard>
        </div>
      </section>
    </div>
    <MessageStack>
      {notice ? <Message message={notice} onOpenChange={(open) => { if (!open) setNotice(null); }} open /> : null}
    </MessageStack>
  </main>;
}
